import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { hasSupabaseConfig, supabase } from './lib/supabase';
import { DashboardShell } from './components/DashboardShell';
import LandingPage from './pages/Landing';
import LoginPage from './pages/Login';
import OnboardingPage from './pages/Onboarding';
import TeamPage from './pages/Team';
import LogsPage from './pages/Logs';
import HealthPage from './pages/Health';
import OverviewPage from './pages/Overview';
import ProfilePage from './pages/Profile';
import WorkspacesPage from './pages/Workspaces';

function LoadingShell({ title, body }: { title: string; body: string }) {
  return (
    <div className="state-shell">
      <div className="state-card">
        <div className="state-pill">FounderPulse</div>
        <h1>{title}</h1>
        <p>{body}</p>
      </div>
    </div>
  );
}

function PublicRoute({
  session,
  hasTeam,
  loading,
  children,
}: {
  session: Session | null;
  hasTeam: boolean | null;
  loading: boolean;
  children: React.ReactNode;
}) {
  if (loading) {
    return <LoadingShell title="Connecting to FounderPulse" body="Restoring your session and workspace access." />;
  }

  if (session) {
    if (hasTeam === false) {
      return <Navigate to="/onboarding" replace />;
    }

    return <Navigate to="/dashboard/overview" replace />;
  }

  return <>{children}</>;
}

function ProtectedRoute({
  session,
  hasTeam,
  loading,
  children,
}: {
  session: Session | null;
  hasTeam: boolean | null;
  loading: boolean;
  children: React.ReactNode;
}) {
  if (loading) {
    return <LoadingShell title="Loading your workspace" body="Checking memberships and daily summaries." />;
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (hasTeam === false) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

function OnboardingRoute({
  session,
  hasTeam,
  loading,
  children,
}: {
  session: Session | null;
  hasTeam: boolean | null;
  loading: boolean;
  children: React.ReactNode;
}) {
  if (loading) {
    return <LoadingShell title="Preparing onboarding" body="Checking whether you already belong to a workspace." />;
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (hasTeam === true) {
    return <Navigate to="/dashboard/overview" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [hasTeam, setHasTeam] = useState<boolean | null>(null);
  const [teamLoading, setTeamLoading] = useState(true);

  useEffect(() => {
    if (!session) {
      setHasTeam(null);
      setTeamLoading(false);
      return;
    }

    const client = supabase;

    if (!client) {
      setHasTeam(null);
      setTeamLoading(false);
      return;
    }

    const userId = session.user.id;

    let cancelled = false;

    async function loadTeamState() {
      setTeamLoading(true);
      try {
        const { data, error } = await client!
          .from('project_members')
          .select('id')
          .eq('user_id', userId)
          .limit(1);

        if (cancelled) {
          return;
        }

        if (error) {
          setHasTeam(false);
          setTeamLoading(false);
          return;
        }

        setHasTeam((data || []).length > 0);
      } finally {
        if (!cancelled) {
          setTeamLoading(false);
        }
      }
    }

    void loadTeamState();

    return () => {
      cancelled = true;
    };
  }, [session]);

  useEffect(() => {
    if (!hasSupabaseConfig() || !supabase) {
      setAuthLoading(false);
      return;
    }

    let mounted = true;

    void supabase.auth.getSession().then(({ data }) => {
      if (!mounted) {
        return;
      }

      setSession(data.session ?? null);
      setAuthLoading(false);
    });

    const { data } = supabase.auth.onAuthStateChange((_event: AuthChangeEvent, nextSession: Session | null) => {
      setSession(nextSession);
      setAuthLoading(false);
    });

    return () => {
      mounted = false;
      data.subscription.unsubscribe();
    };
  }, []);

  if (!hasSupabaseConfig() || !supabase) {
    return (
      <LoadingShell
        title="Connecting to FounderPulse"
        body="Loading the hosted dashboard backend. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY only if you are self-hosting."
      />
    );
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <PublicRoute session={session} hasTeam={hasTeam} loading={authLoading || teamLoading}>
            <LandingPage />
          </PublicRoute>
        }
      />
      <Route
        path="/login"
        element={
          <PublicRoute session={session} hasTeam={hasTeam} loading={authLoading || teamLoading}>
            <LoginPage session={session} supabase={supabase} />
          </PublicRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <OnboardingRoute session={session} hasTeam={hasTeam} loading={authLoading || teamLoading}>
            {session ? <OnboardingPage session={session} supabase={supabase} /> : null}
          </OnboardingRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute session={session} hasTeam={hasTeam} loading={authLoading || teamLoading}>
            <DashboardShell supabase={supabase} session={session} />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<OverviewPage session={session} supabase={supabase} />} />
        <Route path="team" element={<TeamPage session={session} supabase={supabase} />} />
        <Route path="logs" element={<LogsPage session={session} supabase={supabase} />} />
        <Route path="health" element={<HealthPage session={session} supabase={supabase} />} />
        <Route path="workspaces" element={<WorkspacesPage session={session} supabase={supabase} />} />
        <Route path="profile" element={<ProfilePage session={session} supabase={supabase} />} />
        <Route path="*" element={<Navigate to="overview" replace />} />
      </Route>
      <Route path="*" element={<Navigate to={session ? (hasTeam === false ? '/onboarding' : '/dashboard/overview') : '/'} replace />} />
    </Routes>
  );
}